import React, { useCallback, useEffect, useMemo } from 'react';

import { SingleDatePicker } from '@/components/SingleDatePicker';
import { useNavbarSlot } from '@/contexts/NavbarSlot.context';
import { appRoutes } from '@routes';
import { mergeReportFiltersIntoSearch } from '@/utils/reportFiltersUrl';
import { OverallProductivity } from '../DashboardOverviewNew/components/OverallProductivity.component';
import { KeyHighlights } from '../DashboardOverviewNew/components/KeyHighlights.component';
import { ProductivityOverTime } from '../DashboardOverviewNew/components/ProductivityOverTime.component';
import { ActivityBreakdown } from '../DashboardOverviewNew/components/ActivityBreakdown.component';
import { ActivityHeatmap } from '../DashboardOverviewNew/components/ActivityHeatmap.component';
import { TopApplications } from '../DashboardOverviewNew/components/TopApplications.component';
import { FocusVsDistraction } from '../DashboardOverviewNew/components/FocusVsDistraction.component';
import { AIInsights } from '../DashboardOverviewNew/components/AIInsights.component';
import { RecentAlerts } from '../DashboardOverviewNew/components/RecentAlerts.component';
import { TeamPerformance } from '../DashboardOverviewNew/components/TeamPerformance.component';
import { AttendanceSummary } from '../DashboardOverviewNew/components/AttendanceSummary.component';
import { useTeamDashboardData } from './hooks/useTeamDashboardData';
import styles from '../DashboardOverviewNew/DashboardOverviewNew.module.scss';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_OVERALL_PRODUCTIVITY_DATA,
  DEMO_KEY_HIGHLIGHTS_DATA,
  DEMO_CHART_TICKS,
  DEMO_PRODUCTIVITY_OVER_TIME_DATA,
  DEMO_ACTIVITY_BREAKDOWN_DATA,
  DEMO_ACTIVITY_HEATMAP_DATA,
  DEMO_TOP_APPLICATIONS_DATA,
  DEMO_FOCUS_VS_DISTRACTION_DATA,
  DEMO_AI_INSIGHTS_DATA,
  DEMO_RECENT_ALERTS_DATA,
  DEMO_TEAM_PERFORMANCE_DATA,
  DEMO_ATTENDANCE_SUMMARY_DATA,
} from './TeamDashboard.demoData';

export const TeamDashboardComponent = () => {
  const {
    teamId,
    dateRange,
    setDateRange,
    isFetchingStats,
    isFetchingAttendance,
    overallProductivityData,
    keyHighlightsData,
    productivityOverTimeData,
    chartTicks,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    attendanceSummaryData,
    teamPerformanceData,
    heatmapData,
    recentAlertsData,
    insightsData,
  } = useTeamDashboardData();

  const setNavbarSlot = useNavbarSlot();

  const reportFiltersSearch = useMemo(
    () =>
      mergeReportFiltersIntoSearch('', {
        start: dateRange.start,
        end: dateRange.end,
      }),
    [dateRange.start, dateRange.end]
  );

  // Alerts has no date filter, so it only needs the department forwarded —
  // this dashboard has no member selector to forward either (whole-team view).
  const alertsReportSearch = useMemo(
    () => mergeReportFiltersIntoSearch('', { teamId }),
    [teamId]
  );

  const handleDateChange = useCallback(
    (dateStr) => setDateRange({ start: dateStr, end: dateStr }),
    [setDateRange]
  );

  useEffect(() => {
    setNavbarSlot(
      <SingleDatePicker
        date={dateRange.start}
        onDateChange={handleDateChange}
      />
    );
    return () => setNavbarSlot(null);
  }, [dateRange.start, handleDateChange, setNavbarSlot]);

  const displayIsFetchingStats = IS_DEMO_MODE ? false : isFetchingStats;
  const displayIsFetchingAttendance = IS_DEMO_MODE
    ? false
    : isFetchingAttendance;
  const displayOverallProductivityData = IS_DEMO_MODE
    ? DEMO_OVERALL_PRODUCTIVITY_DATA
    : overallProductivityData;
  const displayKeyHighlightsData = IS_DEMO_MODE
    ? DEMO_KEY_HIGHLIGHTS_DATA
    : keyHighlightsData;
  const displayProductivityOverTimeData = IS_DEMO_MODE
    ? DEMO_PRODUCTIVITY_OVER_TIME_DATA
    : productivityOverTimeData;
  const displayChartTicks = IS_DEMO_MODE ? DEMO_CHART_TICKS : chartTicks;
  const displayActivityBreakdownData = IS_DEMO_MODE
    ? DEMO_ACTIVITY_BREAKDOWN_DATA
    : activityBreakdownData;
  const displayHeatmapData = IS_DEMO_MODE
    ? DEMO_ACTIVITY_HEATMAP_DATA
    : heatmapData;
  const displayTopApplicationsData = IS_DEMO_MODE
    ? DEMO_TOP_APPLICATIONS_DATA
    : topApplicationsData;
  const displayFocusVsDistractionData = IS_DEMO_MODE
    ? DEMO_FOCUS_VS_DISTRACTION_DATA
    : focusVsDistractionData;
  const displayInsightsData = IS_DEMO_MODE
    ? DEMO_AI_INSIGHTS_DATA
    : insightsData;
  const displayRecentAlertsData = IS_DEMO_MODE
    ? DEMO_RECENT_ALERTS_DATA
    : (recentAlertsData ?? []);
  const displayTeamPerformanceData = IS_DEMO_MODE
    ? DEMO_TEAM_PERFORMANCE_DATA
    : (teamPerformanceData ?? []);
  const displayAttendanceSummaryData = IS_DEMO_MODE
    ? DEMO_ATTENDANCE_SUMMARY_DATA
    : attendanceSummaryData;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.row}>
        <OverallProductivity
          data={displayOverallProductivityData}
          isLoading={displayIsFetchingStats}
        />
        <KeyHighlights
          data={displayKeyHighlightsData}
          isLoading={displayIsFetchingStats}
        />
      </div>

      <div className={styles.row2}>
        <ProductivityOverTime
          data={displayProductivityOverTimeData}
          isLoading={displayIsFetchingStats}
          chartTicks={displayChartTicks}
        />
        <ActivityBreakdown
          data={displayActivityBreakdownData}
          isLoading={displayIsFetchingStats}
          footerTo={{
            pathname: appRoutes().teamsTimeline,
            search: reportFiltersSearch,
          }}
        />
        <ActivityHeatmap
          data={displayHeatmapData}
          isLoading={displayIsFetchingStats}
        />
      </div>

      <div className={styles.row3}>
        <TopApplications
          data={displayTopApplicationsData}
          isLoading={displayIsFetchingStats}
        />
        <FocusVsDistraction
          data={displayFocusVsDistractionData}
          isLoading={displayIsFetchingStats}
        />
        <AIInsights
          data={displayInsightsData}
          isLoading={displayIsFetchingStats}
        />
        <RecentAlerts
          data={displayRecentAlertsData}
          isLoading={displayIsFetchingStats && !recentAlertsData}
          reportFiltersSearch={alertsReportSearch}
        />
      </div>

      <div className={styles.row4}>
        <TeamPerformance data={displayTeamPerformanceData} />
        <AttendanceSummary
          data={displayAttendanceSummaryData}
          isLoading={displayIsFetchingAttendance}
          reportFiltersSearch={reportFiltersSearch}
        />
      </div>
    </div>
  );
};
